/**
 * Recurring items and their handled occurrences (FR-REC-01 to FR-REC-07).
 *
 * Two collections. `recurring/{id}` holds the definitions; `recurringHandled/{key}` holds
 * one small document per occurrence that has been posted or dismissed.
 *
 * **The handled record is what makes "Due now" a worklist rather than a projection.**
 * Occurrences themselves are derived — twelve months of them is a computation, not a
 * table — but *which* have been dealt with is a fact, and the only place it can live is
 * the database. Storing occurrences instead would mean writing 12 documents per item per
 * year for rows that mostly never get touched.
 */

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { DateKey } from '../core/dates';
import type { AccountTypeLookup } from '../core/rollups';
import { toTransactionInput, type Occurrence, type RecurringItem } from '../core/recurring';
import type { Paise } from '../core/money';
import { auditUpdate } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';
import { createTransaction } from './transactions';

const ITEMS = 'recurring';
const HANDLED = 'recurringHandled';

export class RecurringError extends Error {}

function toItem(id: string, data: Record<string, unknown>): RecurringItem {
  return { ...(data as Omit<RecurringItem, 'id'>), id };
}

export function watchRecurring(onChange: (items: RecurringItem[]) => void): () => void {
  return onSnapshot(
    collection(db, ITEMS),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((d) => toItem(d.id, d.data()))
          .sort((a, b) => a.description.localeCompare(b.description)),
      ),
    () => onChange([]),
  );
}

export interface HandledOccurrence {
  key: string;
  /** `reopened` is a dismissal that was undone — the marker stays, the effect lifts. */
  outcome: 'posted' | 'dismissed' | 'reopened';
  transactionId: string | null;
  handledOn: DateKey;
}

/** The keys that should be hidden from "Due now". A reopened one is due again. */
export function handledKeys(handled: Map<string, HandledOccurrence>): Set<string> {
  return new Set(
    [...handled.values()].filter((h) => h.outcome !== 'reopened').map((h) => h.key),
  );
}

/**
 * Which occurrences are already dealt with.
 *
 * Watched whole rather than queried by month: one document per handled occurrence means
 * roughly 30 a month, and the list is read on every visit to the Due screen. A year of
 * them is ~360 documents, which is one small read against a 50,000/day allowance
 * (ARCHITECTURE §1.3).
 */
export function watchHandled(onChange: (handled: Map<string, HandledOccurrence>) => void): () => void {
  return onSnapshot(
    collection(db, HANDLED),
    (snapshot) =>
      onChange(
        new Map(
          snapshot.docs.map((d) => [d.id, { key: d.id, ...(d.data() as Omit<HandledOccurrence, 'key'>) }]),
        ),
      ),
    () => onChange(new Map()),
  );
}

export async function saveRecurring(
  item: Omit<RecurringItem, 'id'> & { id?: string },
  by: string,
): Promise<string> {
  if (!item.description.trim()) throw new RecurringError('A recurring item needs a description.');
  if (item.type === 'transfer' && !item.toAccountId) {
    throw new RecurringError('A recurring transfer needs a destination account.');
  }

  const ref = item.id ? doc(db, ITEMS, item.id) : doc(collection(db, ITEMS));
  const { id: _id, ...rest } = item;

  await trackWrite(
    setDoc(
      ref,
      { ...rest, updatedAt: serverTimestamp(), ...(item.id ? {} : { createdAt: serverTimestamp(), enteredBy: by }) },
      { merge: Boolean(item.id) },
    ),
  );

  return ref.id;
}

export async function setRecurringArchived(
  item: RecurringItem,
  archived: boolean,
  by: string,
): Promise<void> {
  await trackWrite(updateDoc(doc(db, ITEMS, item.id), { archived, updatedAt: serverTimestamp() }));
  await auditUpdate(ITEMS, item.id, { archived: item.archived }, { archived }, by);
}

/**
 * Post an occurrence (FR-REC-04).
 *
 * `amountPaise` comes from the caller, not from the occurrence, because FR-REC-03's whole
 * point is that what posts may differ from what was estimated — and the estimate on the
 * item is deliberately left alone.
 *
 * The transaction is written first and the handled marker second. That order matters: a
 * failure between them leaves the occurrence still showing as due, which the user can
 * see and act on. The reverse would leave a paid bill silently marked handled.
 */
export async function postOccurrence(
  occurrence: Occurrence,
  amountPaise: Paise,
  dateKey: DateKey,
  by: string,
  accountType: AccountTypeLookup,
): Promise<string> {
  if (!Number.isSafeInteger(amountPaise) || amountPaise <= 0) {
    throw new RecurringError('Enter an amount to post.');
  }
  if (!occurrence.accountId) {
    throw new RecurringError('Choose the account this is paid from.');
  }

  const transactionId = await createTransaction(
    toTransactionInput(occurrence, amountPaise, dateKey),
    by,
    accountType,
  );

  await markHandled(occurrence.key, 'posted', transactionId, dateKey);
  return transactionId;
}

/** Dismissing marks the occurrence handled without creating anything (FR-REC-04). */
export async function dismissOccurrence(occurrence: Occurrence, on: DateKey): Promise<void> {
  await markHandled(occurrence.key, 'dismissed', null, on);
}

async function markHandled(
  key: string,
  outcome: HandledOccurrence['outcome'],
  transactionId: string | null,
  handledOn: DateKey,
): Promise<void> {
  await trackWrite(
    setDoc(doc(db, HANDLED, key), { outcome, transactionId, handledOn, at: serverTimestamp() }),
  );
}

/**
 * Undo a dismissal, putting the occurrence back on the list.
 *
 * Only a dismissal: a posted occurrence has a transaction behind it, and lifting the
 * marker while leaving the transaction would offer the same bill for payment twice.
 *
 * Written as a new outcome rather than deleted — hard delete is denied to every client
 * on every collection (`firestore.rules`), and there is no reason for this to be the
 * exception. The record that it was once dismissed is worth keeping anyway.
 */
export async function undismiss(handled: HandledOccurrence, on: DateKey): Promise<void> {
  if (handled.outcome === 'posted') {
    throw new RecurringError('That one was posted. Delete the transaction to undo it.');
  }
  await trackWrite(
    setDoc(
      doc(db, HANDLED, handled.key),
      { outcome: 'reopened', reopenedOn: on, at: serverTimestamp() },
      { merge: true },
    ),
  );
}

/**
 * Seed recurring items from an agreed draft.
 *
 * Takes the items rather than reading the workbook: `RECURRING-DRAFT.md` is edited by the
 * user first, exactly as the category draft was (FR-CAT-05's "do not invent one" applies
 * to this list for the same reason).
 */
export async function seedRecurring(items: Omit<RecurringItem, 'id'>[], by: string): Promise<number> {
  const existing = await getDocs(collection(db, ITEMS));
  if (!existing.empty) {
    throw new RecurringError(`Recurring items already exist (${existing.size}).`);
  }

  const batch = writeBatch(db);
  for (const item of items) {
    batch.set(doc(collection(db, ITEMS)), {
      ...item,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    });
  }
  await trackWrite(batch.commit());

  return items.length;
}
